import {
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
} from '@angular/core';

import { InteractionEntity } from '../../../coaching-base/config';
import {
    FocusDto,
    InteractionSummaryDto,
} from '../../../coaching-base/general';
import { ObservationFormValue } from '../../../coaching-commitment/commitment-observations/observations.commitment.form';
import { composeTagIdentifierNext } from '../../../coaching-tags/tags/tags.model';
import { StateToProps } from '../../../store';
import {
    disuseStore,
    pluck,
    useDispatcher,
    useStore,
} from '../../../store.decorator';
import { TranslationKeyMap } from '../../coaching-observation.i18n';
import { InteractionFormValue } from '../../observation-interaction/interaction-form-value.model';

import {
    recalculate,
    reload,
    reloadCancel,
} from './observation.focus.summary.action';
import focusSummaryReducer from './observation.focus.summary.reducer';
import { FocusSummaryState } from './observation.focus.summary.state';

@Component({
    selector: 'cmps-observation-focus-summary',
    templateUrl: './observation.focus.summary.component.html',
    host: {
        class: 'cmps-observation-focus-summary',
    },
})
export class ObservationFocusSummary
    implements OnChanges, OnInit, OnDestroy, StateToProps<FocusSummaryState>
{
    state: FocusSummaryState;
    @Input() storePathPrefix: string;

    /** @private */ _i18n = TranslationKeyMap;
    @Input() mode: 'observation' | 'commitment';
    @Input() observationId: number;
    @Input() published: boolean;
    @Input() performerId: number;
    @Input() interactionEntity: InteractionEntity;
    @Input() focus: FocusDto;
    @Input() interactions: InteractionFormValue[];
    @Input() hideSubSummary: boolean;
    @Input() commitmentObservations: ObservationFormValue[];

    private _prevFocus: string;
    private _prevInteractions: string;

    constructor() {
        this.mode = 'observation';
    }

    ngOnChanges(changes: SimpleChanges): void {
        // eslint-disable-next-line @typescript-eslint/dot-notation
        if (
            // eslint-disable-next-line @typescript-eslint/dot-notation
            (changes['focus'] && !changes['focus'].firstChange) ||
            // eslint-disable-next-line @typescript-eslint/dot-notation
            (changes['interactions'] && !changes['interactions'].firstChange)
        ) {
            const differs = this._differs();
            if (differs.focus) {
                this._reload();
            } else if (differs.interactions) {
                this._recalculate();
            }
        }

        // eslint-disable-next-line @typescript-eslint/dot-notation
        if (changes['commitmentObservations']) {
            this.interactions = pluck<InteractionSummaryDto[]>(
                this.commitmentObservations,
                'focusContainers[].interactions[]',
            )
                .filter((e) => e.scoring)
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                .map<InteractionFormValue>(
                    (e) =>
                        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                        ({
                            scoring: [e.scoring],
                        }) as InteractionFormValue,
                );
            const differs = this._differs();
            if (differs.interactions) {
                this._recalculate();
            }
        }
    }

    ngOnInit(): void {
        // need to access a specific key for state (array like)
        useStore(
            this,
            `${this.storePathPrefix}.focusSummary__${composeTagIdentifierNext(
                this.focus,
            )}_${Math.random()}`,
            focusSummaryReducer.focusSummary,
        );
    }

    ngOnDestroy(): void {
        disuseStore(this);
    }

    propsToDispatch(): void {
        this._differs();
        this.mode === 'commitment' ? this._recalculate() : this._reload();
    }

    private _differFocus(): boolean {
        const next = composeTagIdentifierNext(this.focus);
        const diff = this._prevFocus !== next;
        this._prevFocus = next;
        return diff;
    }

    private _differInteractions(): boolean {
        const next = (this.interactions || []).reduce(
            (p, c) => `${p}:${JSON.stringify(c.scoring)}`,
            '',
        );
        const diff = this._prevInteractions !== next;
        this._prevInteractions = next;
        return diff;
    }

    private _differs(): { focus: boolean; interactions: boolean } {
        return {
            focus: this._differFocus(),
            interactions: this._differInteractions(),
        };
    }

    private _reload(): void {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (this.focus && this.focus.behaviourId != null) {
            useDispatcher(this, reloadCancel({}));
            useDispatcher(
                this,
                reload({
                    performerId: this.performerId,
                    behaviourId: this.focus.behaviourId,
                    subBehaviourId: this.focus.subBehaviourId,
                    observationId: this.observationId,
                    interactionEntity: this.interactionEntity,
                    published: this.published,
                    interactions: this.interactions,
                }),
            );
        }
    }

    private _recalculate(): void {
        useDispatcher(
            this,
            recalculate({
                mode: this.mode,
                interactionEntity: this.interactionEntity,
                published: this.published,
                interactions: this.interactions,
            }),
        );
    }
}
